import { Alert, Box, Button, Progress, Snackbar, Stack, Typography, useTheme } from "@carrotfertility/carotene-core"
import React, { Dispatch, ReactElement, SetStateAction, useMemo, useState } from "react"
import { CarrotPlanFormOption, MenuItemOptionFields, TopicOptionFields } from "./hooks/useCarrotPlanMappings"
import { CarrotPlanMediaItem, CarrotPlanMediaType } from "../../types/CarrotPlanOptions"
import { CarrotPlanMediaTypeId, CarrotPlanMediaTypes } from "./MediaTypes"
import { CarrotPlanFormStep, FieldName } from "./CarrotPlanFormTypes"
import { useFormContext, useWatch } from "@carrotfertility/carotene-core-x"
import { getAutocompleteMediaItemOption, getAutocompleteTopicOption } from "./utils/autocompleteTransformations"
import { RecommendationDisplaySection } from "./Recommendations"
import { useGetAllRecommendedItems } from "./hooks/useGetAllRecommendedItems"
import { useAssistantEnabledJourney } from "./hooks/useAssistantEnabledJourney"
import { Journeys } from "../../types/Journeys"

function RecommendationsContainer({ children }: { children: React.ReactNode }): ReactElement {
  const theme = useTheme()

  return (
    <Stack
      sx={{
        width: "376px",
        height: "fit-content",
        maxHeight: "1018px",
        border: 1,
        borderRadius: theme.tokens.borderRadius.md,
        borderColor: theme.palette.border.light
      }}
      spacing={theme.tokens.spacing.lg}
      padding={(theme) => theme.spacing(theme.tokens.spacing.lg)}
    >
      {children}
    </Stack>
  )
}

function RecommendationsHeader(): ReactElement {
  return <Typography variant="h4">Recommendations</Typography>
}

function CenteredBox({ children, ...props }: React.ComponentProps<typeof Box>): ReactElement {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" {...props}>
      {children}
    </Box>
  )
}

function RecommendationsLoadingPlaceholder(): ReactElement {
  return (
    <RecommendationsContainer>
      <RecommendationsHeader />
      <CenteredBox padding={(theme) => theme.spacing(theme.tokens.spacing.xl)}>
        <Progress />
      </CenteredBox>
    </RecommendationsContainer>
  )
}

function RecommendationsSelectStageMessage(): ReactElement {
  return (
    <RecommendationsContainer>
      <RecommendationsHeader />
      <CenteredBox>
        <Typography variant="body2">Select primary journey phase to see recommendations</Typography>
      </CenteredBox>
    </RecommendationsContainer>
  )
}

export type AddItemToPlanParams = {
  menuItem: CarrotPlanFormOption<MenuItemOptionFields>
  updateRecommendedItems:
    | Dispatch<SetStateAction<CarrotPlanFormOption<MenuItemOptionFields>[]>>
    | Dispatch<SetStateAction<CarrotPlanMediaItem[]>>
  mediaItem: CarrotPlanMediaItem
}

export type Recommendation = {
  contentItem?: CarrotPlanMediaItem
  menuItem: CarrotPlanFormOption<MenuItemOptionFields>
  updateRecommendedItems:
    | Dispatch<SetStateAction<CarrotPlanFormOption<MenuItemOptionFields>[]>>
    | Dispatch<SetStateAction<CarrotPlanMediaItem[]>>
  label: string
}

type AdditionalRecommendationProps = {
  menuItemOptions: CarrotPlanFormOption<MenuItemOptionFields>[]
  topicOptions: CarrotPlanFormOption<TopicOptionFields>[]
  isLoading?: boolean
}

function CarrotPlanAssistantAdditionalRecommendations({
  menuItemOptions,
  topicOptions,
  isLoading = false
}: AdditionalRecommendationProps): ReactElement {
  const steps: CarrotPlanFormStep[] = useWatch({ name: FieldName.steps })
  const { setValue } = useFormContext()
  const {
    allRecommendedArticles,
    allRecommendedVideos,
    allRecommendedGuides,
    allRecommendedGroups,
    allRecommendedPersonalInteractionMenuItems,
    allRecommendedSelfServeResourceMenuItems,
    allRecommendedFinancialInformationMenuItems,
    readAndLearnMenuItems,
    areThereRecommendations
  } = useGetAllRecommendedItems({ menuItemOptions, topicOptions, steps })

  const [recommendedPersonalInteractionMenuItems, setRecommendedPersonalInteractionMenuItems] = useState(
    allRecommendedPersonalInteractionMenuItems
  )
  const [recommendedFinancialInformationMenuItems, setRecommendedFinancialInformationMenuItems] = useState(
    allRecommendedFinancialInformationMenuItems
  )
  const [recommendedSelfServeResourceMenuItems, setRecommendedSelfServeResourceMenuItems] = useState(
    allRecommendedSelfServeResourceMenuItems
  )
  const [recommendedArticles, setRecommendedArticles] = useState(allRecommendedArticles)
  const [recommendedVideos, setRecommendedVideos] = useState(allRecommendedVideos)
  const [recommendedGuides, setRecommendedGuides] = useState(allRecommendedGuides)
  const [recommendedGroups, setRecommendedGroups] = useState(allRecommendedGroups)
  let nextArticleIndex = 0

  const getRecommendationToDisplay = (
    recommendedItems: CarrotPlanFormOption<MenuItemOptionFields>[],
    updateRecommendedItemsFunction: Dispatch<SetStateAction<CarrotPlanFormOption<MenuItemOptionFields>[]>>
  ): Recommendation[] => {
    if (recommendedItems.length) {
      return [
        {
          menuItem: recommendedItems[0],
          updateRecommendedItems: updateRecommendedItemsFunction,
          label: recommendedItems[0].label
        }
      ]
    }

    return []
  }

  const personalInteractionsToDisplay: Recommendation[] = useMemo(() => {
    const recommendations = []

    if (recommendedGroups.length) {
      recommendations.push({
        contentItem: recommendedGroups[0],
        menuItem: readAndLearnMenuItems.find(
          (item) => item.optionFields.entityId === CarrotPlanMediaTypes[CarrotPlanMediaTypeId.Group].menuItemId
        ),
        updateRecommendedItems: setRecommendedGroups,
        label: `Attend a group session on "${recommendedGroups[0].title}"`
      })
    }
    if (recommendedPersonalInteractionMenuItems.length) {
      recommendations.push({
        menuItem: recommendedPersonalInteractionMenuItems[0],
        updateRecommendedItems: setRecommendedPersonalInteractionMenuItems,
        label: recommendedPersonalInteractionMenuItems[0].label
      })
    }

    return recommendations
  }, [recommendedGroups, recommendedPersonalInteractionMenuItems, readAndLearnMenuItems])

  const contentToDisplay: Recommendation[] = useMemo(() => {
    const contentRecommendations = []

    if (recommendedGuides.length) {
      contentRecommendations.push({
        contentItem: recommendedGuides[0],
        menuItem: readAndLearnMenuItems.find(
          (item) => item.optionFields.entityId === CarrotPlanMediaTypes[CarrotPlanMediaTypeId.Guide].menuItemId
        ),
        updateRecommendedItems: setRecommendedGuides,
        label: `Explore "${recommendedGuides[0].title}"`
      })
    }
    if (recommendedVideos.length) {
      contentRecommendations.push({
        contentItem: recommendedVideos[0],
        menuItem: readAndLearnMenuItems.find(
          (item) => item.optionFields.entityId === CarrotPlanMediaTypes[CarrotPlanMediaTypeId.Video].menuItemId
        ),
        updateRecommendedItems: setRecommendedVideos,
        label: `Watch "${recommendedVideos[0].title}"`
      })
    }
    // Fill out the three spots with as many articles as needed, put articles first in list of all content
    let recommendationCount = contentRecommendations.length
    const articleRecommendations = []
    while (recommendationCount < 3 && recommendedArticles.length > nextArticleIndex) {
      if (recommendedArticles.length) {
        articleRecommendations.push({
          contentItem: recommendedArticles[nextArticleIndex],
          menuItem: readAndLearnMenuItems.find(
            (item) => item.optionFields.entityId === CarrotPlanMediaTypes[CarrotPlanMediaTypeId.Article].menuItemId
          ),
          updateRecommendedItems: setRecommendedArticles,
          label: `Read "${recommendedArticles[nextArticleIndex].title}"`
        })
        nextArticleIndex++
        recommendationCount++
      }
    }

    return [...articleRecommendations, ...contentRecommendations]
  }, [recommendedArticles, recommendedGuides, nextArticleIndex, readAndLearnMenuItems, recommendedVideos])

  const allRecommendationSections = [
    { recommendations: personalInteractionsToDisplay, sectionLabel: "Chats & interactions" },
    { recommendations: contentToDisplay, sectionLabel: "Read and learn resources" },
    {
      recommendations: getRecommendationToDisplay(
        recommendedSelfServeResourceMenuItems,
        setRecommendedSelfServeResourceMenuItems
      ),
      sectionLabel: "Self-serve resources"
    },
    {
      recommendations: getRecommendationToDisplay(
        recommendedFinancialInformationMenuItems,
        setRecommendedFinancialInformationMenuItems
      ),
      sectionLabel: "Financial information"
    }
  ]

  const refreshRecommendations = () => {
    setRecommendedPersonalInteractionMenuItems(recommendedPersonalInteractionMenuItems.slice(1))
    setRecommendedSelfServeResourceMenuItems(recommendedSelfServeResourceMenuItems.slice(1))
    setRecommendedFinancialInformationMenuItems(recommendedFinancialInformationMenuItems.slice(1))
    setRecommendedArticles(recommendedArticles.slice(nextArticleIndex))
    nextArticleIndex = 0
    setRecommendedVideos(recommendedVideos.slice(1))
    setRecommendedGuides(recommendedGuides.slice(1))
    setRecommendedGroups(recommendedGroups.slice(1))
  }

  const resetRecommendations = () => {
    setRecommendedPersonalInteractionMenuItems(allRecommendedPersonalInteractionMenuItems)
    setRecommendedSelfServeResourceMenuItems(allRecommendedSelfServeResourceMenuItems)
    setRecommendedFinancialInformationMenuItems(allRecommendedFinancialInformationMenuItems)
    setRecommendedArticles(allRecommendedArticles)
    setRecommendedVideos(allRecommendedVideos)
    setRecommendedGuides(allRecommendedGuides)
    setRecommendedGroups(allRecommendedGroups)
  }

  const dismissItem = ({ menuItem, updateRecommendedItems, mediaItem = null }: AddItemToPlanParams) => {
    if (mediaItem) {
      updateRecommendedItems((currentItems) =>
        currentItems.filter((item: CarrotPlanMediaItem) => item.entryId != mediaItem.entryId)
      )
      if (mediaItem.contentType === CarrotPlanMediaType.Article) {
        nextArticleIndex--
      }
    } else {
      updateRecommendedItems((currentItems) =>
        currentItems.filter(
          (item: CarrotPlanFormOption<MenuItemOptionFields>) =>
            item.optionFields.entityId != menuItem.optionFields.entityId
        )
      )
    }
  }

  const [snackbarState, setSnackbarState] = useState<{ open: boolean; message: string | null; error: boolean }>({
    open: false,
    error: false,
    message: null
  })
  const handleSnackbarClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbarState((previous) => ({ open: false, message: previous.message, error: previous.error }))
  }

  function getNextAvailableStep() {
    for (const step of steps) {
      if (step.menuItem == null) {
        return step
      }
    }
    return null
  }

  const createInputNameWithPrefix = (stepIndex: number, fieldName: string) =>
    `${FieldName.steps}.${stepIndex}.${fieldName}`

  const findMediaItemTopic = (mediaItem) =>
    topicOptions.find(function (option) {
      return option.label == mediaItem.subCategory && option.group == mediaItem.categoryTitle
    })

  const addItemToPlan = ({ menuItem, updateRecommendedItems, mediaItem }: AddItemToPlanParams) => {
    const step = getNextAvailableStep()
    if (step == null) {
      setSnackbarState(() => ({
        open: true,
        error: true,
        message: "Remove an item from the Carrot Plan and try again"
      }))
      return
    }
    setValue(createInputNameWithPrefix(step.stepIndex, FieldName.menuItem), menuItem)
    setValue(createInputNameWithPrefix(step.stepIndex, FieldName.url), menuItem?.optionFields?.defaultUrl)
    if (mediaItem) {
      setValue(
        createInputNameWithPrefix(step.stepIndex, FieldName.topic),
        getAutocompleteTopicOption(menuItem.label, findMediaItemTopic(mediaItem), true)
      )
      setValue(createInputNameWithPrefix(step.stepIndex, FieldName.content), getAutocompleteMediaItemOption(mediaItem))
    }
    dismissItem({ menuItem, updateRecommendedItems, mediaItem })
  }

  const noMoreRecommendations = useMemo((): boolean => {
    return (
      !recommendedPersonalInteractionMenuItems.length &&
      !recommendedSelfServeResourceMenuItems.length &&
      !recommendedFinancialInformationMenuItems.length &&
      !recommendedArticles.length &&
      !recommendedVideos.length &&
      !recommendedGuides.length &&
      !recommendedGroups.length
    )
  }, [
    recommendedArticles.length,
    recommendedFinancialInformationMenuItems.length,
    recommendedGroups.length,
    recommendedGuides.length,
    recommendedPersonalInteractionMenuItems.length,
    recommendedSelfServeResourceMenuItems.length,
    recommendedVideos.length
  ])

  return areThereRecommendations || isLoading ? (
    <RecommendationsContainer>
      <RecommendationsHeader />
      {isLoading ? (
        <CenteredBox>
          <Progress />
        </CenteredBox>
      ) : noMoreRecommendations ? (
        <Typography variant="body1" align="center">
          No more recommendations
        </Typography>
      ) : (
        <>
          {allRecommendationSections.map((section, index) => (
            <RecommendationDisplaySection
              key={index}
              recommendations={section.recommendations}
              typeLabel={section.sectionLabel}
              addItemToPlan={addItemToPlan}
              dismissItem={dismissItem}
            />
          ))}
        </>
      )}
      {!isLoading && (
        <Box display="flex" justifyContent="center" padding={(theme) => theme.spacing(theme.tokens.spacing.lg)}>
          <Button
            variant="contained"
            color="secondary"
            onClick={noMoreRecommendations ? () => resetRecommendations() : () => refreshRecommendations()}
          >
            {noMoreRecommendations ? "See all recommendations" : "Get more recommendations"}
          </Button>
        </Box>
      )}
      <Snackbar open={snackbarState.open} onClose={handleSnackbarClose} autoHideDuration={5000}>
        <Alert onClose={handleSnackbarClose} severity={snackbarState.error ? "error" : "success"}>
          {snackbarState.message}
        </Alert>
      </Snackbar>
    </RecommendationsContainer>
  ) : null
}

type AssistantRecommendationsPanelProps = {
  benefitEnabledJourneyData: Journeys
  menuItemOptions: CarrotPlanFormOption<MenuItemOptionFields>[]
  topicOptions: CarrotPlanFormOption<TopicOptionFields>[]
  isFetchingOptions: boolean
}

export function AssistantRecommendationsPanel({
  benefitEnabledJourneyData,
  menuItemOptions,
  topicOptions,
  isFetchingOptions
}: AssistantRecommendationsPanelProps): ReactElement | null {
  const selectedPrimaryJourney = useWatch({ name: FieldName.primaryJourney })
  const selectedPrimaryJourneyPhase = useWatch({ name: FieldName.primaryJourneyPhase })

  const { isSelectedJourneyAssistantEnabled, isSelectedStageAssistantEnabled } = useAssistantEnabledJourney(
    benefitEnabledJourneyData,
    selectedPrimaryJourney,
    selectedPrimaryJourneyPhase
  )

  if (!isSelectedJourneyAssistantEnabled) return null
  if (!isSelectedStageAssistantEnabled) return <RecommendationsSelectStageMessage />
  if (isFetchingOptions) return <RecommendationsLoadingPlaceholder />

  return (
    <CarrotPlanAssistantAdditionalRecommendations
      key={`recommendations-${selectedPrimaryJourney}-${selectedPrimaryJourneyPhase}`}
      menuItemOptions={menuItemOptions}
      topicOptions={topicOptions}
    />
  )
}
